import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, AlertTriangle, Loader2, RotateCcw, Upload, X, PauseCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtBytes, media } from "@/lib/api";
import { uploadFile } from "./Uploader";

const STATUS = {
  done: { label: "Réussi", color: "#5d9a35", Icon: CheckCircle2 },
  failed: { label: "Échec", color: "#EF4444", Icon: AlertTriangle },
  processing: { label: "Optimisation…", color: "#EAB308", Icon: Loader2 },
  complete: { label: "Optimisation…", color: "#EAB308", Icon: Loader2 },
  uploading: { label: "Interrompu", color: "#F97316", Icon: PauseCircle },
};

const Row = ({ u, onRetry, onResume, onDismiss, live }) => {
  const s = STATUS[u.status] || STATUS.failed;
  const pct = live ? Math.round((live.bytes / u.size) * 100) : Math.round((u.received / u.total_chunks) * 100);
  return (
    <div className="flex flex-col gap-3 border border-white/10 bg-[#111] p-4 sm:flex-row sm:items-center" data-testid={`upload-history-row-${u.upload_id}`}>
      <div className="h-14 w-20 shrink-0 overflow-hidden bg-[#0b0b0b]">{u.thumb && <img src={media(u.thumb)} alt="" className="h-full w-full object-cover" />}</div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-white">{u.filename}{u.replace && <span className="ml-2 font-mono text-[10px] text-white/50">remplacement</span>}</div>
        <div className="mt-1 font-mono text-[11px] text-white/55">
          {fmtBytes(u.size)} · {u.category} · {new Date(u.created_at).toLocaleString("fr-FR")}
          {u.photo_title && ` · « ${u.photo_title} »`}{u.photo_deleted && " (retirée du site)"}
        </div>
        {u.error && <div className="mt-1 text-xs text-red-400" data-testid={`upload-history-error-${u.upload_id}`}>{u.error}</div>}
        {u.status === "uploading" && <div className="mt-2 h-1 w-full max-w-xs bg-white/10"><div className="h-full bg-[#F97316]" style={{ width: `${pct}%` }} /></div>}
        {live && <div className="mt-1 font-mono text-[11px] text-white/70">{live.phase === "uploading" ? `Reprise… ${pct}%` : live.phase}</div>}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <span className="flex items-center gap-1.5 font-mono text-[11px]" style={{ color: s.color }} data-testid={`upload-history-status-${u.upload_id}`}>
          <s.Icon size={14} className={s.Icon === Loader2 ? "animate-spin" : ""} /> {s.label}{u.status === "uploading" ? ` ${pct}%` : ""}
        </span>
        {u.status === "failed" && u.can_retry && (
          <button onClick={() => onRetry(u)} data-testid={`upload-history-retry-${u.upload_id}`} className="flex items-center gap-1.5 bg-white px-3 py-2 text-xs font-semibold text-black"><RotateCcw size={13} /> Relancer</button>
        )}
        {(u.status === "uploading" || (u.status === "failed" && !u.can_retry)) && !live && (
          <button onClick={() => onResume(u)} data-testid={`upload-history-resume-${u.upload_id}`} className="flex items-center gap-1.5 border border-white/30 px-3 py-2 text-xs text-white hover:border-white"><Upload size={13} /> {u.status === "uploading" ? "Reprendre" : "Renvoyer"}</button>
        )}
        {u.status !== "processing" && !live && (
          <button onClick={() => onDismiss(u)} title="Retirer de l'historique" data-testid={`upload-history-dismiss-${u.upload_id}`} className="text-white/40 hover:text-white"><X size={15} /></button>
        )}
      </div>
    </div>
  );
};

export const UploadHistory = ({ onChange }) => {
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState("all");
  const [live, setLive] = useState({});
  const pick = useRef(null);
  const target = useRef(null);
  const load = useCallback(() => api.get("/admin/uploads").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const busy = (list || []).some((u) => ["processing", "complete"].includes(u.status));
    if (!busy) return undefined;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [list, load]);

  const retry = async (u) => {
    try { await api.post(`/admin/uploads/${u.upload_id}/retry`); toast.success("Traitement relancé"); load(); }
    catch (e) { toast.error(errMsg(e)); load(); }
  };
  const resume = (u) => { target.current = u; pick.current?.click(); };
  const onPicked = async (file) => {
    const u = target.current;
    if (!file || !u) return;
    if (file.size !== u.size) { toast.error(`Ce n'est pas le même fichier (${fmtBytes(file.size)} au lieu de ${fmtBytes(u.size)}).`); return; }
    const control = { paused: false, cancelled: false };
    const set = (s) => setLive((l) => ({ ...l, [u.upload_id]: s }));
    try {
      const again = u.status !== "uploading" && u.photo_id && !u.photo_deleted ? u.photo_id : undefined;
      await uploadFile(file, { category: u.category, resumeId: u.status === "uploading" ? u.upload_id : undefined, replaceId: again, control, onState: set });
      toast.success(`${u.filename} publié`);
      if (u.status !== "uploading") await api.delete(`/admin/uploads/${u.upload_id}`).catch(() => {});
      onChange?.();
    } catch (e) { toast.error(errMsg(e)); }
    setLive((l) => { const n = { ...l }; delete n[u.upload_id]; return n; });
    load();
  };
  const dismiss = async (u) => { await api.delete(`/admin/uploads/${u.upload_id}`).catch(() => {}); load(); };

  const counts = (list || []).reduce((a, u) => { const k = u.status === "complete" ? "processing" : u.status; a[k] = (a[k] || 0) + 1; return a; }, {});
  const shown = (list || []).filter((u) => filter === "all" || u.status === filter || (filter === "processing" && u.status === "complete"));
  const failedRetryable = (list || []).filter((u) => u.status === "failed" && u.can_retry);
  return (
    <div data-testid="upload-history">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {[["all", "Tous", list?.length || 0], ["done", "Réussis", counts.done || 0], ["failed", "Échecs", counts.failed || 0], ["uploading", "Interrompus", counts.uploading || 0], ["processing", "En cours", counts.processing || 0]].map(([k, l, n]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`upload-history-filter-${k}`}
              className={`rounded-full border px-3 py-1.5 text-xs ${filter === k ? "border-white bg-white text-black" : "border-white/20 text-white/75"}`}>{l} ({n})</button>
          ))}
        </div>
        <div className="flex gap-2">
          {failedRetryable.length > 1 && <button onClick={() => failedRetryable.forEach(retry)} data-testid="upload-history-retry-all" className="flex items-center gap-1.5 bg-white px-3 py-2 text-xs font-semibold text-black"><RotateCcw size={13} /> Tout relancer ({failedRetryable.length})</button>}
          <button onClick={load} data-testid="upload-history-refresh" className="flex items-center gap-1.5 border border-white/20 px-3 py-2 text-xs text-white"><RefreshCw size={13} /> Actualiser</button>
        </div>
      </div>
      <p className="mb-4 text-xs text-white/50">« Relancer » refait l'optimisation sans renvoyer le fichier (conservé 48 h sur le serveur). « Reprendre » / « Renvoyer » vous demande de choisir le même fichier : seuls les morceaux manquants sont envoyés.</p>
      {list && !shown.length && <p className="text-sm text-white/55" data-testid="upload-history-empty">Aucun envoi dans cette catégorie.</p>}
      <div className="space-y-2">{shown.map((u) => <Row key={u.upload_id} u={u} live={live[u.upload_id]} onRetry={retry} onResume={resume} onDismiss={dismiss} />)}</div>
      <input ref={pick} type="file" hidden data-testid="upload-history-file-input" onChange={(e) => { onPicked(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
};
